import { useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../primitives/accordion';
import { Button } from '../../primitives/button';
import { Card, CardContent } from '../../primitives/card';
import { Separator } from '../../primitives/separator';
import { cn } from '../../utils/cn';
import { RecurrenceEditor } from './components/recurrence-editor';
import { RelativeDateField } from './components/relative-date-field';
import { ScheduleSummary } from './components/schedule-summary';
import { TimeZoneSelect } from './components/time-zone-select';
import { DEFAULT_TIME_ZONE } from './constants';
import {
  RepeatPreset,
  ScheduleDateOrRelative,
  ScheduleReference,
  WorkflowScheduleValue,
} from './types';
import { createDefaultRecurringValue, createDefaultScheduledValue } from './utils/parse-value';
import { resolveDateOrRelative } from './utils/relative-date';

export * from './types';

interface WorkflowScheduleProps {
  accordionChildren?: {
    content?: React.ReactNode;
    trigger?: React.ReactNode;
  };
  className?: {
    content?: string;
    trigger?: string;
  };
  disabled?: boolean;
  id?: string;
  /** Base for relative dates; defaults to "now". "previous" only allows relative dates. */
  reference?: ScheduleReference;
  /** Describe the first previous-based action relative to its trigger, keeping the stored reference. */
  relativeToTrigger?: boolean;
  value: WorkflowScheduleValue | null | undefined;
  onChange: (value: WorkflowScheduleValue) => void;
}

/**
 * Controlled editor for a `WorkflowSchedule` value: a one-time (`scheduled`) execution or a
 * (`recurring`) window driven by a cron expression.
 *
 * @example
 * ```tsx
 * const DEFAULT_WORKFLOW_SCHEDULE_VALUE: WorkflowScheduleValue = {
 * type: 'scheduled',
 *   timeZone: 'America/Mexico_City',
 *   executeAt: {
 *     reference: 'now',
 *   },
 * };
 *
 * const [value, setValue] = useState<WorkflowScheduleValue | null>(DEFAULT_WORKFLOW_SCHEDULE_VALUE);
 *
 * <WorkflowSchedule value={value} onChange={setValue} />
 * ```
 */
export function WorkflowSchedule({
  accordionChildren,
  className,
  disabled = false,
  id,
  reference = 'now',
  relativeToTrigger = false,
  value,
  onChange,
}: WorkflowScheduleProps) {
  const [open, setOpen] = useState<string>('');

  const initialSchedule = value ?? createDefaultScheduledValue(undefined, { reference });
  function withReference(date: ScheduleDateOrRelative, isEnd = false): ScheduleDateOrRelative {
    if (reference === 'now') return date;
    return typeof date === 'string'
      ? { reference, ...(isEnd ? { increments: [{ value: 1, unit: 'months' as const }] } : {}) }
      : { ...date, reference };
  }
  const schedule: WorkflowScheduleValue =
    initialSchedule.type === 'scheduled'
      ? { ...initialSchedule, executeAt: withReference(initialSchedule.executeAt) }
      : {
          ...initialSchedule,
          startsAt: withReference(initialSchedule.startsAt),
          endsAt: withReference(initialSchedule.endsAt, true),
        };
  const timeZone = schedule.timeZone ?? DEFAULT_TIME_ZONE;
  const startsAt = schedule.type === 'scheduled' ? schedule.executeAt : schedule.startsAt;
  const startDate = resolveDateOrRelative(startsAt, timeZone) ?? new Date();

  function handleStartChange(nextStart: ScheduleDateOrRelative) {
    onChange(
      schedule.type === 'scheduled'
        ? { ...schedule, executeAt: nextStart }
        : { ...schedule, startsAt: nextStart },
    );
  }

  function handleRepeatChange(_preset: RepeatPreset, expression: string | undefined) {
    if (!expression) {
      onChange(createDefaultScheduledValue(timeZone, startsAt));
      return;
    }

    if (schedule.type === 'recurring') {
      onChange({ ...schedule, recurrence: { type: 'cron', expression } });
      return;
    }

    onChange(createDefaultRecurringValue(timeZone, startsAt, expression, reference));
  }

  return (
    <Card className="rounded-lg shadow-xs">
      <CardContent className="p-0">
        <Accordion collapsible type="single" value={open} onValueChange={setOpen}>
          <AccordionItem className="border-none" value="edit">
            <AccordionContent className="p-0">
              <div className={cn('grid gap-6 p-4', className?.content)}>
                {accordionChildren?.content ?? null}

                <div className="relative border-l-2 border-accent pb-1 pl-4">
                  <RelativeDateField
                    allowImmediate
                    disabled={disabled}
                    id={id ? `${id}-starts-at` : undefined}
                    label="Inicia"
                    reference={reference}
                    relativeToTrigger={relativeToTrigger}
                    onChange={handleStartChange}
                    timeZone={timeZone}
                    value={startsAt}
                  />
                </div>

                <div
                  className={cn('rounded-r-lg border-l-2 py-3 pr-2 pl-4', {
                    'border-success bg-success/[0.05]': schedule.type === 'recurring',
                  })}
                >
                  <RecurrenceEditor
                    disabled={disabled}
                    expression={
                      schedule.type === 'recurring' ? schedule.recurrence.expression : undefined
                    }
                    id={id ? `${id}-recurrence` : undefined}
                    onChange={handleRepeatChange}
                    startDate={startDate}
                  />
                </div>

                {schedule.type === 'recurring' && (
                  <div className="border-l-2 border-accent pb-1 pl-4">
                    <RelativeDateField
                      allowImmediate={false}
                      disabled={disabled}
                      fromDate={startDate}
                      id={id ? `${id}-ends-at` : undefined}
                      label="Termina"
                      reference={reference}
                      relativeToTrigger={relativeToTrigger}
                      onChange={(endsAt) => onChange({ ...schedule, endsAt })}
                      timeZone={timeZone}
                      value={schedule.endsAt}
                    />
                  </div>
                )}

                <TimeZoneSelect
                  disabled={disabled}
                  id={id ? `${id}-timezone` : undefined}
                  onChange={(nextTimeZone) => onChange({ ...schedule, timeZone: nextTimeZone })}
                  value={timeZone}
                />
              </div>

              <Separator />
            </AccordionContent>

            <AccordionTrigger
              asChild
              className={cn('cursor-pointer rounded-lg p-0', {
                'rounded-t-none bg-background': open === 'edit',
              })}
            >
              <Button
                title="Editar"
                className={cn(
                  'grid size-full justify-start rounded-[inherit] bg-transparent p-4 text-left whitespace-normal text-primary',
                  className?.trigger,
                )}
                variant="secondary"
              >
                {accordionChildren?.trigger ?? null}

                <ScheduleSummary relativeToTrigger={relativeToTrigger} value={schedule} />
              </Button>
            </AccordionTrigger>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
